import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Zap, 
  Download, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Trash2,
  RotateCcw,
  ExternalLink,
  PlayCircle,
  Shield,
  Activity,
  Terminal,
  FileText,
  Users
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import api, { endpoints, downloadFile } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import "react-datepicker/dist/react-datepicker.css";

const CursorApiManager = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [importHistory, setImportHistory] = useState([]); // Start empty - only load when user requests
  const [dataStats, setDataStats] = useState(null); // Start null - only load when user requests
  const [activeTab, setActiveTab] = useState('import');
  
  // Import state
  const [importType, setImportType] = useState('recent');
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 7)),
    endDate: endOfDay(new Date())
  });
  const [recentDays, setRecentDays] = useState(7);
  
  // Nuclear operations state
  const [nukeType, setNukeType] = useState('all');
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [showNukeConfirm, setShowNukeConfirm] = useState(false);
  const [nukeDateRange, setNukeDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 30)),
    endDate: endOfDay(new Date())
  });
  
  // Activity log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [showActivityLog, setShowActivityLog] = useState(true);
  
  // Test results state for dry run display
  const [testResults, setTestResults] = useState(null);
  
  // Database status state
  const [databaseStatus, setDatabaseStatus] = useState(null);

  // Activity logging function
  const addActivityLog = useCallback((type, message, details = null) => {
    const timestamp = new Date();
    const newLog = {
      id: Date.now() + Math.random(),
      timestamp,
      type, // 'info', 'success', 'warning', 'error'
      message,
      details
    };
    
    setActivityLogs(prev => [newLog, ...prev.slice(0, 99)]); // Keep last 100 logs
  }, []);

  const testConnection = useCallback(async () => {
    try {
      setLoading(true);
      addActivityLog('info', 'Fetching team members from Cursor API...');
      
      const response = await api.get(endpoints.cursor.testConnection);
      
      if (response.data.success) {
        const { memberCount, teamMembers } = response.data;
        
        setConnectionStatus({
          status: 'connected',
          memberCount: memberCount,
          message: response.data.message
        });
        
        // Log successful retrieval
        addActivityLog('success', `✅ Successfully retrieved ${memberCount} team members`, {
          totalMembers: memberCount,
          sampleMembers: teamMembers.slice(0, 5).map(member => `${member.name} (${member.email}) - ${member.role}`).join('\n'),
          note: memberCount > 5 ? `Showing first 5 of ${memberCount} members` : 'All members shown'
        });
        
        // Show breakdown by role
        const roleBreakdown = teamMembers.reduce((acc, member) => {
          acc[member.role] = (acc[member.role] || 0) + 1;
          return acc;
        }, {});
        
        addActivityLog('info', '👥 Team Member Breakdown by Role', {
          roles: Object.entries(roleBreakdown).map(([role, count]) => `${role}: ${count}`).join('\n'),
          totalUsers: memberCount
        });
        
        toast.success(`Found ${memberCount} team members!`);
      } else {
        setConnectionStatus({
          status: 'error',
          message: response.data.message
        });
        addActivityLog('error', 'Failed to retrieve team members', response.data.error || response.data.message);
        toast.error('Failed to get team members');
      }
    } catch (error) {
      setConnectionStatus({
        status: 'error',
        message: error.response?.data?.message || error.message
      });
      addActivityLog('error', 'Error getting team members', error.response?.data?.message || error.message);
      toast.error('Failed to get team members');
    } finally {
      setLoading(false);
    }
  }, [addActivityLog, setLoading, setConnectionStatus]);

  const loadImportHistory = useCallback(async () => {
    try {
      const response = await api.get(endpoints.cursor.importHistory);
      setImportHistory(response.data.imports || []);
    } catch (error) {
      // Silently handle errors on initial load (expected during setup)
      setImportHistory([]);
      // Only log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Import history not available (expected during initial setup):', error.response?.status);
      }
    }
  }, [setImportHistory]);

  const loadDataStats = useCallback(async () => {
    try {
      const response = await api.get(endpoints.cursor.dataStats);
      setDataStats(response.data.stats);
    } catch (error) {
      // Silently handle errors on initial load (expected during setup)
      setDataStats({
        totalDailyRecords: 0,
        totalUsers: 0,
        totalTeams: 0,
        totalImports: 0,
        totalUploads: 0,
        oldestRecord: null,
        newestRecord: null
      });
      // Only log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Data stats not available (expected during initial setup):', error.response?.status);
      }
    }
  }, [setDataStats]);

  // Remove automatic initialization - page should be idle until user takes action
  // const loadInitialData = useCallback(async () => {
  //   addActivityLog('info', 'Initializing Data Import page...');
  //   await Promise.all([
  //     loadImportHistory(),
  //     loadDataStats()
  //   ]);
  //   addActivityLog('success', 'Data Import page initialized successfully');
  // }, [addActivityLog, loadImportHistory, loadDataStats]);

  // useEffect(() => {
  //   loadInitialData();
  // }, [loadInitialData]);




  const handleManualImport = async () => {
    try {
      setLoading(true);
      
      const requestBody = importType === 'recent' 
        ? { days: recentDays }
        : {
            startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
            endDate: format(dateRange.endDate, 'yyyy-MM-dd')
          };
      
      const endpoint = importType === 'recent' 
        ? endpoints.cursor.importRecent 
        : endpoints.cursor.importManual;
      
      const importDesc = importType === 'recent' 
        ? `recent ${recentDays} days` 
        : `${format(dateRange.startDate, 'MMM dd')} to ${format(dateRange.endDate, 'MMM dd')}`;
      
      addActivityLog('info', `Starting manual import for ${importDesc}...`);
      addActivityLog('info', `Request body: ${JSON.stringify(requestBody)}`);
      
      const response = await api.post(endpoint, requestBody);
      
      if (response.data.success) {
        const { importId, status, message, note } = response.data;
        
        if (status === 'running') {
          addActivityLog('info', `Import started successfully (ID: ${importId})`, {
            status: 'running',
            importId: importId,
            note: note || 'Import is running in the background'
          });
          toast.success(message);
          
          // Immediately refresh import history to show the pending record
          await loadImportHistory();
          
          // Start polling for completion (optional - for better UX)
          // startImportStatusPolling(importId);
        } else {
          // Legacy response format (for backwards compatibility)
          const stats = response.data.stats;
          addActivityLog('success', `Import completed successfully!`, {
            processed: stats?.processedRecords || 0,
            newUsers: stats?.newUsers || 0,
            newTeams: stats?.newTeams || 0,
            importId: response.data.importId
          });
          toast.success(response.data.message);
          await loadImportHistory();
          await loadDataStats();
        }
      } else {
        addActivityLog('error', 'Import returned unsuccessful response', response.data.message || 'Unknown error');
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Import error details:', error);
      
      let errorDetails = 'Unknown error';
      if (error.response) {
        // Server responded with an error status
        errorDetails = {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        };
        addActivityLog('error', `Server error (${error.response.status})`, JSON.stringify(errorDetails.data || errorDetails.statusText));
      } else if (error.request) {
        // Request was made but no response received
        addActivityLog('error', 'Network error - no response from server', 'Check your connection and server status');
        errorDetails = 'Network error - no response from server';
      } else {
        // Error in setting up the request
        addActivityLog('error', 'Request setup error', error.message);
        errorDetails = error.message;
      }
      
      const displayMsg = error.response?.data?.message || error.message || 'Import failed';
      toast.error(displayMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleTestImport = async () => {
    try {
      setLoading(true);
      addActivityLog('info', 'Starting API connectivity test (dry run - no data saved)...');
      
      const response = await api.post(endpoints.cursor.testImport, { days: 1 });
      
      if (response.data.success) {
        const apiTestResults = response.data;
        const stats = apiTestResults.stats;
        
        // Store test results for display in results pane
        setTestResults(apiTestResults);
        
        // Log the test results in detail
        addActivityLog('success', '🧪 API Test Completed - Data Retrieved Successfully!', {
          targetDate: `${apiTestResults.dateRange.targetDate} (${apiTestResults.dateRange.dayName})`,
          totalUsers: stats.totalUsers,
          totalTeams: stats.totalTeams,
          dailyRecords: stats.totalDailyRecords,
          usageEvents: stats.totalUsageEvents,
          note: 'NO DATA WAS SAVED - This was a connectivity test only'
        });
        
        // Show team members results if available
        if (apiTestResults.teamMembers && !apiTestResults.teamMembers.error) {
          const members = apiTestResults.teamMembers;
          addActivityLog('info', `👥 Team Members: Found ${members.totalUsers} users and ${members.totalTeams} teams`, {
            sampleUsers: members.users?.slice(0, 3).map(u => u.email).join(', ') || 'None',
            sampleTeams: members.teams?.slice(0, 3).map(t => t.name).join(', ') || 'None'
          });
        } else if (apiTestResults.teamMembers?.error) {
          addActivityLog('warning', '👥 Team Members: Failed to retrieve', apiTestResults.teamMembers.error);
        }
        
        // Show daily usage results if available
        if (apiTestResults.dailyUsage && !apiTestResults.dailyUsage.error) {
          const usage = apiTestResults.dailyUsage;
          if (usage.totalRecords > 0) {
            addActivityLog('info', `📊 Daily Usage: Found ${usage.totalRecords} records`, {
              dateRange: usage.dateRange ? `${usage.dateRange.startDate} to ${usage.dateRange.endDate}` : 'Various dates',
              sampleData: `${usage.sampleRecords?.length || 0} sample records retrieved`
            });
          } else {
            addActivityLog('warning', '📊 Daily Usage: No data found for the specified date range');
          }
        } else if (apiTestResults.dailyUsage?.error) {
          addActivityLog('warning', '📊 Daily Usage: Failed to retrieve', apiTestResults.dailyUsage.error);
        }
        
        // Show usage events results if available
        if (apiTestResults.usageEvents && !apiTestResults.usageEvents.error) {
          const events = apiTestResults.usageEvents;
          if (events.totalEvents > 0) {
            addActivityLog('info', `📝 Usage Events: Found ${events.totalEvents} events (sample)`, {
              eventTypes: events.eventTypes?.join(', ') || 'Unknown',
              languages: events.languages?.slice(0, 5).join(', ') || 'Various',
              sampleSize: `${events.sampleEvents?.length || 0} events retrieved`
            });
          } else {
            addActivityLog('warning', '📝 Usage Events: No events found for the specified date range');
          }
        } else if (apiTestResults.usageEvents?.error) {
          addActivityLog('warning', '📝 Usage Events: Failed to retrieve', apiTestResults.usageEvents.error);
        }
        
        // Display full debug logs if available
        if (apiTestResults.debugLogs && apiTestResults.debugLogs.length > 0) {
          addActivityLog('info', '🔍 Full Debug Log Output:', {
            debugLogs: apiTestResults.debugLogs.join('\n'),
            totalLogEntries: apiTestResults.debugLogs.length
          });
        }
        
        toast.success('🧪 API Test completed! Check activity log for full debug details. No data was saved to database.');
        
        // Don't load import history or data stats since this was just a test
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'API connectivity test failed';
      addActivityLog('error', '🧪 API Test Failed', errorMsg);
      toast.error(`API Test Failed: ${errorMsg}`);
      setTestResults(null); // Clear any previous test results on error
    } finally {
      setLoading(false);
    }
  };

  const checkDatabaseStatus = async () => {
    try {
      setLoading(true);
      addActivityLog('info', 'Checking database table record counts...');
      
      const response = await api.get(endpoints.cursor.databaseStatus);
      
      if (response.data.success) {
        const status = response.data.status;
        setDatabaseStatus(status);
        
        // Log the results
        addActivityLog('success', '📊 Database Status Check Completed', {
          dailyRecords: `${status.dailyRecords.totalRecords.toLocaleString()} records (${status.dailyRecords.uniqueUsers} unique users)`,
          usageEvents: `${status.usageEvents.totalEvents.toLocaleString()} events (${status.usageEvents.uniqueUsers} unique users)`,
          users: `${status.users.totalUsers} total users`,
          teams: `${status.teams.totalTeams} teams`,
          imports: `${status.imports.completedImports}/${status.imports.totalImports} successful imports`,
          diagnosis: status.diagnosis.dataFlowIssue ? 'DATA FLOW ISSUE DETECTED!' : 'Data flow appears normal'
        });
        
        // Special warning for data flow issues
        if (status.diagnosis.dataFlowIssue) {
          addActivityLog('warning', '⚠️ Data Flow Issue Detected!', {
            issue: 'Usage Events are being imported but Daily Records table is empty',
            impact: 'Dashboard/Analytics/Teams pages will show no data',
            solution: 'Daily Records need to be aggregated from Usage Events'
          });
        }
        
        toast.success('Database status check completed');
      } else {
        addActivityLog('error', 'Database status check failed', response.data.error || 'Unknown error');
        toast.error('Database status check failed');
      }
    } catch (error) {
      console.error('Database status check error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Database status check failed';
      addActivityLog('error', 'Database status check failed', errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (format, type) => {
    try {
      setExporting(true);
      
      const params = {
        startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
        endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
        format: format,
        type: type
      };

      await downloadFile(
        `${endpoints.analytics.export}?${new URLSearchParams(params)}`,
        `analytics-${type}-${format(new Date(), 'yyyy-MM-dd')}.${format}`
      );
      
      toast.success(`Analytics (${type}) exported as ${format.toUpperCase()}`);
      addActivityLog('success', `Exported ${type} data as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
      addActivityLog('error', `Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleNuclearOperation = async () => {
    try {
      setLoading(true);
      
      let endpoint, requestBody;
      
      if (nukeType === 'all') {
        endpoint = endpoints.cursor.nukeAll;
        requestBody = { confirmationPhrase };

      } else if (nukeType === 'dateRange') {
        endpoint = endpoints.cursor.nukeDateRange;
        requestBody = {
          startDate: format(nukeDateRange.startDate, 'yyyy-MM-dd'),
          endDate: format(nukeDateRange.endDate, 'yyyy-MM-dd'),
          confirmationPhrase
        };
      }
      
      console.log('🚨 Nuclear operation request:', { endpoint, requestBody });
      const response = await api.post(endpoint, requestBody);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowNukeConfirm(false);
        setConfirmationPhrase('');
        await loadDataStats();
        await loadImportHistory();
      }
    } catch (error) {
      console.error('❌ Nuclear operation failed:', error);
      console.error('❌ Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Nuclear operation failed';
      toast.error(errorMessage);
      addActivityLog('error', 'Nuclear operation failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getExpectedConfirmationPhrase = () => {
    if (nukeType === 'all') return 'DELETE ALL ANALYTICS DATA';

    if (nukeType === 'dateRange') {
      return `DELETE ${format(nukeDateRange.startDate, 'yyyy-MM-dd')} TO ${format(nukeDateRange.endDate, 'yyyy-MM-dd')}`;
    }
    return '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'running': return <Activity className="w-4 h-4 text-blue-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-8 h-8 text-blue-600" />
              Cursor API Integration
            </h1>
            <p className="text-gray-600 mt-1">Direct integration with Cursor's Admin API for automated data imports</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className="flex items-center gap-2">
              {connectionStatus?.status === 'connected' && (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-green-700">{connectionStatus.memberCount} members</span>
                </>
              )}
              {connectionStatus?.status === 'error' && (
                <>
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-red-700">Connection Error</span>
                </>
              )}
            </div>
            
            <button
              onClick={testConnection}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              Get Members
            </button>
          </div>
        </div>
        
        <div className="mt-4">
          <a 
            href="https://docs.cursor.com/en/account/teams/admin-api" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Cursor Admin API Documentation
          </a>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Tabs and Content */}
        <div className="lg:col-span-2">
          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
            {[
              { id: 'import', label: 'Data Import', icon: Download },
              { id: 'status', label: 'Database Status', icon: Activity },
              { id: 'history', label: 'Import History', icon: Clock },
              { id: 'nuclear', label: 'Data Management', icon: Shield }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
              </nav>
            </div>

            <div className="p-6">
          {/* Data Import Tab */}
          {activeTab === 'import' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Data from Cursor API</h3>
                
                {/* Import Type Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Import Type</label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="recent"
                          checked={importType === 'recent'}
                          onChange={(e) => setImportType(e.target.value)}
                          className="mr-2"
                        />
                        Recent Days
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="manual"
                          checked={importType === 'manual'}
                          onChange={(e) => setImportType(e.target.value)}
                          className="mr-2"
                        />
                        Date Range
                      </label>
                    </div>
                  </div>

                  {/* Recent Days Input */}
                  {importType === 'recent' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Days (max 90)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="90"
                        value={recentDays}
                        onChange={(e) => setRecentDays(parseInt(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {/* Date Range Picker */}
                  {importType === 'manual' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <DatePicker
                          selected={dateRange.startDate}
                          onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          dateFormat="MMM d, yyyy"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <DatePicker
                          selected={dateRange.endDate}
                          onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          dateFormat="MMM d, yyyy"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={handleManualImport}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? <LoadingSpinner size="small" /> : <Download className="w-4 h-4" />}
                    Start Import
                  </button>
                  
                  <button
                    onClick={handleTestImport}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <PlayCircle className="w-4 h-4" />
                    Test API Connection (Dry Run)
                  </button>
                </div>
              </div>

              {/* Data Stats */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">Current Data Statistics</h4>
                  <button
                    onClick={loadDataStats}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Load Stats
                  </button>
                </div>
                {dataStats ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{dataStats.totalDailyRecords?.toLocaleString() || 0}</div>
                        <div className="text-sm text-gray-600">Daily Records</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{dataStats.totalUsers?.toLocaleString() || 0}</div>
                        <div className="text-sm text-gray-600">Developers</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{dataStats.totalTeams?.toLocaleString() || 0}</div>
                        <div className="text-sm text-gray-600">Teams</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">{dataStats.totalImports?.toLocaleString() || 0}</div>
                        <div className="text-sm text-gray-600">Imports</div>
                      </div>
                    </div>
                    {dataStats.oldestRecord && (
                      <div className="mt-3 text-sm text-gray-600">
                        Data range: {formatDate(dataStats.oldestRecord)} to {formatDate(dataStats.newestRecord)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">Click "Load Stats" to view current data statistics</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Database Status Tab */}
          {activeTab === 'status' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Database Status</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={checkDatabaseStatus}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <Activity className="w-4 h-4" />
                    {loading ? 'Checking...' : 'Check Status'}
                  </button>
                </div>
              </div>

              {databaseStatus ? (
                <div className="space-y-6">
                  {/* Status Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* Daily Records Status */}
                    <div className={`rounded-lg border-2 p-4 ${
                      databaseStatus.dailyRecords.isEmpty ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">Daily Records Table</h4>
                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                          databaseStatus.dailyRecords.isEmpty ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {databaseStatus.dailyRecords.isEmpty ? 'EMPTY' : 'HAS DATA'}
                        </div>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div>📊 <strong>{databaseStatus.dailyRecords.totalRecords.toLocaleString()}</strong> total records</div>
                        <div>👥 <strong>{databaseStatus.dailyRecords.uniqueUsers}</strong> unique users</div>
                        {databaseStatus.dailyRecords.dateRange.earliest && (
                          <div>📅 {new Date(databaseStatus.dailyRecords.dateRange.earliest).toLocaleDateString()} - {new Date(databaseStatus.dailyRecords.dateRange.latest).toLocaleDateString()}</div>
                        )}
                      </div>
                      {databaseStatus.dailyRecords.isEmpty && (
                        <div className="mt-2 text-xs text-red-600">
                          ⚠️ Dashboard/Analytics/Teams pages query this table
                        </div>
                      )}
                    </div>

                    {/* Usage Events Status */}
                    <div className={`rounded-lg border-2 p-4 ${
                      databaseStatus.usageEvents.isEmpty ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">Usage Events Table</h4>
                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                          databaseStatus.usageEvents.isEmpty ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {databaseStatus.usageEvents.isEmpty ? 'EMPTY' : 'HAS DATA'}
                        </div>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div>📝 <strong>{databaseStatus.usageEvents.totalEvents.toLocaleString()}</strong> total events</div>
                        <div>👥 <strong>{databaseStatus.usageEvents.uniqueUsers}</strong> unique users</div>
                        {databaseStatus.usageEvents.dateRange.earliest && (
                          <div>📅 {new Date(databaseStatus.usageEvents.dateRange.earliest).toLocaleDateString()} - {new Date(databaseStatus.usageEvents.dateRange.latest).toLocaleDateString()}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Diagnosis */}
                  {databaseStatus.diagnosis.dataFlowIssue && (
                    <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        <h4 className="font-semibold text-yellow-900">Data Flow Issue Detected!</h4>
                      </div>
                      <div className="text-sm text-yellow-800 space-y-1">
                        <div><strong>Problem:</strong> Usage Events are being imported but Daily Records table is empty</div>
                        <div><strong>Impact:</strong> Dashboard, Analytics, and Teams pages will show no data</div>
                        <div><strong>Cause:</strong> Missing data aggregation step from Usage Events → Daily Records</div>
                        <div><strong>Solution:</strong> Need to implement data aggregation process</div>
                      </div>
                    </div>
                  )}

                  {/* Additional Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-blue-600">{databaseStatus.users.totalUsers}</div>
                      <div className="text-sm text-gray-600">Total Users</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-purple-600">{databaseStatus.teams.totalTeams}</div>
                      <div className="text-sm text-gray-600">Teams</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-green-600">{databaseStatus.imports.completedImports}</div>
                      <div className="text-sm text-gray-600">Successful Imports</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-orange-600">{databaseStatus.imports.totalImports}</div>
                      <div className="text-sm text-gray-600">Total Imports</div>
                    </div>
                  </div>

                  {/* Debug Information */}
                  {databaseStatus.debug && databaseStatus.debug.errors.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <h4 className="font-semibold text-red-900 mb-2">Query Errors Detected:</h4>
                      <div className="text-sm text-red-800 space-y-1">
                        {databaseStatus.debug.errors.map((error, index) => (
                          <div key={index}>• Query {error.queryIndex + 1}: {error.error}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Last Updated */}
                  <div className="text-xs text-gray-500 text-center">
                    Last checked: {new Date(databaseStatus.timestamp).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium">Database Status Check</p>
                  <p className="text-sm">Click "Check Status" to view current database table record counts</p>
                  <p className="text-xs mt-2">This will help diagnose why Dashboard/Analytics pages might be empty</p>
                </div>
              )}
            </div>
          )}

          {/* Import History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Import History</h3>
                <button
                  onClick={loadImportHistory}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {importHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Range</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {importHistory.map((import_record, index) => (
                        <tr key={import_record.ImportId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(import_record.Status)}
                              <span className={`text-sm font-medium ${
                                import_record.Status === 'completed' ? 'text-green-700' :
                                import_record.Status === 'failed' ? 'text-red-700' :
                                import_record.Status === 'pending' ? 'text-blue-700' :
                                import_record.Status === 'running' ? 'text-blue-700' :
                                'text-yellow-700'
                              }`}>
                                {import_record.Status}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{import_record.ImportType}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(import_record.StartDate)} - {formatDate(import_record.EndDate)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {import_record.ProcessedRecords?.toLocaleString() || 0}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(import_record.StartedAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {import_record.CreatedBy || 'System'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No import history loaded</p>
                  <p className="text-xs">Click "Refresh" to load import history</p>
                </div>
              )}
            </div>
          )}

          {/* Nuclear Operations Tab */}
          {activeTab === 'nuclear' && (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-semibold text-red-900">Danger Zone</h3>
                </div>
                <p className="text-red-700 text-sm">
                  These operations permanently delete data and cannot be undone. Use with extreme caution.
                </p>
              </div>

              <div className="space-y-6">
                {/* Export Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h4>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => exportData('csv', 'overview')}
                      disabled={exporting}
                      className="btn-outline btn-sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Overview (CSV)
                    </button>
                    <button
                      onClick={() => exportData('xlsx', 'overview')}
                      disabled={exporting}
                      className="btn-outline btn-sm"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Export Overview (Excel)
                    </button>
                    <button
                      onClick={() => exportData('csv', 'users')}
                      disabled={exporting}
                      className="btn-outline btn-sm"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Export Users (CSV)
                    </button>
                  </div>
                </div>

                {/* Delete Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Delete Data</h4>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="dateRange"
                        checked={nukeType === 'dateRange'}
                        onChange={(e) => setNukeType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Delete Data by Date Range</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="all"
                        checked={nukeType === 'all'}
                        onChange={(e) => setNukeType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-red-600 font-medium">🚨 Delete ALL Analytics Data (NUCLEAR)</span>
                    </label>
                  </div>
                </div>

                {nukeType === 'dateRange' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <DatePicker
                        selected={nukeDateRange.startDate}
                        onChange={(date) => setNukeDateRange(prev => ({ ...prev, startDate: date }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        dateFormat="MMM d, yyyy"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <DatePicker
                        selected={nukeDateRange.endDate}
                        onChange={(date) => setNukeDateRange(prev => ({ ...prev, endDate: date }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        dateFormat="MMM d, yyyy"
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setShowNukeConfirm(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Proceed with Deletion
                </button>
              </div>

              {/* Confirmation Modal */}
              {showNukeConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                      <h3 className="text-lg font-semibold text-red-900">Confirm Deletion</h3>
                    </div>
                    
                    <p className="text-gray-700 mb-4">
                      Type the exact phrase below to confirm this irreversible action:
                    </p>
                    
                    <div className="bg-gray-100 p-3 rounded-lg mb-4">
                      <code className="text-sm font-mono">{getExpectedConfirmationPhrase()}</code>
                    </div>
                    
                    <input
                      type="text"
                      value={confirmationPhrase}
                      onChange={(e) => setConfirmationPhrase(e.target.value)}
                      placeholder="Type confirmation phrase..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
                    />
                    
                    <div className="flex gap-3">
                      <button
                        onClick={handleNuclearOperation}
                        disabled={loading || confirmationPhrase !== getExpectedConfirmationPhrase()}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? <LoadingSpinner size="small" /> : 'Delete Data'}
                      </button>
                      <button
                        onClick={() => {
                          setShowNukeConfirm(false);
                          setConfirmationPhrase('');
                        }}
                        className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            </div>
          </div>
        </div>
        
        {/* Right Column - Activity Log */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Activity Log</h3>
                </div>
                <button
                  onClick={() => setShowActivityLog(!showActivityLog)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  {showActivityLog ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">Real-time import progress and system events</p>
            </div>
            
            {showActivityLog && (
              <div className="p-4">
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {activityLogs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No activity yet</p>
                      <p className="text-xs">Events will appear here during imports</p>
                    </div>
                  ) : (
                    activityLogs.map((log) => (
                      <div key={log.id} className="border-l-4 pl-3 py-2 border-gray-200 hover:bg-gray-50 rounded-r">
                        <div className={`border-l-4 -ml-3 pl-3 ${
                          log.type === 'success' ? 'border-green-500' :
                          log.type === 'error' ? 'border-red-500' :
                          log.type === 'warning' ? 'border-yellow-500' :
                          'border-blue-500'
                        }`}>
                          <div className="flex items-start gap-2">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              log.type === 'success' ? 'bg-green-500' :
                              log.type === 'error' ? 'bg-red-500' :
                              log.type === 'warning' ? 'bg-yellow-500' :
                              'bg-blue-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{log.message}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {log.timestamp.toLocaleTimeString()}
                              </p>
                              {log.details && (
                                <div className="mt-2 text-xs text-gray-600">
                                  {typeof log.details === 'string' ? (
                                    <p className="bg-gray-100 p-2 rounded text-xs font-mono">{log.details}</p>
                                  ) : (
                                    <div className="bg-gray-100 p-2 rounded">
                                      {log.details.processed && <div>Records: {log.details.processed}</div>}
                                      {log.details.newUsers !== undefined && <div>New Developers: {log.details.newUsers}</div>}
                                      {log.details.newTeams !== undefined && <div>New Teams: {log.details.newTeams}</div>}
                                      {log.details.importId && <div className="text-xs text-gray-500 mt-1">ID: {log.details.importId}</div>}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {activityLogs.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => setActivityLogs([])}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear logs
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Test Results Pane - Shows dry run results */}
      {testResults && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-blue-600" />
              API Test Results (Dry Run)
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Test Date: {testResults.dateRange?.targetDate} ({testResults.dateRange?.dayName})
            </p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Stats Overview Cards */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Developers</p>
                    <p className="text-2xl font-bold text-blue-900">{testResults.stats?.totalUsers || 0}</p>
                  </div>
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Total Teams</p>
                    <p className="text-2xl font-bold text-green-900">{testResults.stats?.totalTeams || 0}</p>
                  </div>
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4 text-green-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Daily Records</p>
                    <p className="text-2xl font-bold text-purple-900">{testResults.stats?.totalDailyRecords || 0}</p>
                  </div>
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Usage Events</p>
                    <p className="text-2xl font-bold text-orange-900">{testResults.stats?.totalUsageEvents || 0}</p>
                  </div>
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <PlayCircle className="w-4 h-4 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Results - Data Tables */}
            <div className="space-y-6">
              {/* Team Members Table */}
              {testResults.teamMembers && !testResults.teamMembers.error && testResults.teamMembers.users && testResults.teamMembers.users.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    Team Members ({testResults.teamMembers.users.length} records)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-300">
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Email</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Team</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Is Active</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testResults.teamMembers.users.map((user, idx) => (
                          <tr key={idx} className="border-b border-gray-200 hover:bg-gray-100">
                            <td className="py-2 px-3 font-mono text-xs">{user.email}</td>
                            <td className="py-2 px-3">{user.teamName || 'Unknown'}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {user.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-600">{user.source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Daily Usage Table */}
              {testResults.dailyUsage && !testResults.dailyUsage.error && testResults.dailyUsage.sampleRecords && testResults.dailyUsage.sampleRecords.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Daily Usage Data ({testResults.dailyUsage.totalRecords} total records, showing {testResults.dailyUsage.sampleRecords.length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-300">
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">User Email</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">Lines Added</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">Lines Accepted</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">Total Applies</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">Total Accepts</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Most Used Model</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testResults.dailyUsage.sampleRecords.map((record, idx) => (
                          <tr key={idx} className="border-b border-gray-200 hover:bg-gray-100">
                            <td className="py-2 px-3 font-mono text-xs">{record.recordDate}</td>
                            <td className="py-2 px-3 font-mono text-xs">{record.userEmail}</td>
                            <td className="py-2 px-3 text-right font-medium">{record.TotalLinesAdded || 0}</td>
                            <td className="py-2 px-3 text-right font-medium">{record.ChatAcceptedLinesAdded || 0}</td>
                            <td className="py-2 px-3 text-right font-medium">{record.ChatTotalApplies || 0}</td>
                            <td className="py-2 px-3 text-right font-medium">{record.ChatTotalAccepts || 0}</td>
                            <td className="py-2 px-3 text-gray-600">{record.mostUsedModel || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Usage Events Table */}
              {testResults.usageEvents && !testResults.usageEvents.error && testResults.usageEvents.sampleEvents && testResults.usageEvents.sampleEvents.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <PlayCircle className="w-4 h-4 text-orange-600" />
                    Usage Events ({testResults.usageEvents.totalEvents} total events, showing {testResults.usageEvents.sampleEvents.length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-300">
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Timestamp</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">User Email</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Event Type</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Language</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">Lines Added</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-700">Lines Deleted</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Model Used</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-700">Accepted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testResults.usageEvents.sampleEvents.map((event, idx) => (
                          <tr key={idx} className="border-b border-gray-200 hover:bg-gray-100">
                            <td className="py-2 px-3 font-mono text-xs">{new Date(event.eventTimestamp).toLocaleString()}</td>
                            <td className="py-2 px-3 font-mono text-xs">{event.userEmail}</td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                                {event.eventType}
                              </span>
                            </td>
                            <td className="py-2 px-3">{event.language || 'N/A'}</td>
                            <td className="py-2 px-3 text-right font-medium">{event.linesAdded || 0}</td>
                            <td className="py-2 px-3 text-right font-medium">{event.linesDeleted || 0}</td>
                            <td className="py-2 px-3 text-gray-600">{event.modelUsed || 'N/A'}</td>
                            <td className="py-2 px-3 text-center">
                              {event.wasAccepted ? (
                                <span className="text-green-600 font-bold">✓</span>
                              ) : (
                                <span className="text-gray-400">✗</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Error States */}
            {(testResults.teamMembers?.error || testResults.dailyUsage?.error || testResults.usageEvents?.error) && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Test Errors
                </h4>
                <div className="space-y-1 text-sm text-red-700">
                  {testResults.teamMembers?.error && (
                    <div>• Team Members: {testResults.teamMembers.error}</div>
                  )}
                  {testResults.dailyUsage?.error && (
                    <div>• Daily Usage: {testResults.dailyUsage.error}</div>
                  )}
                  {testResults.usageEvents?.error && (
                    <div>• Usage Events: {testResults.usageEvents.error}</div>
                  )}
                </div>
              </div>
            )}

            {/* Schema Validation Results */}
            {testResults.schemaValidation && !testResults.schemaValidation.error && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  Schema Validation Report
                </h4>
                
                {/* Overall Coverage Summary */}
                <div className="mb-4 p-3 bg-white rounded border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Overall API Coverage</span>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      testResults.schemaValidation.summary.coverage >= 90 
                        ? 'bg-green-100 text-green-700' 
                        : testResults.schemaValidation.summary.coverage >= 75 
                        ? 'bg-yellow-100 text-yellow-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {testResults.schemaValidation.summary.coverage}%
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {testResults.schemaValidation.summary.mappedFields} of {testResults.schemaValidation.summary.totalAPIFields} API fields mapped to database
                    {testResults.schemaValidation.summary.missingFields > 0 && (
                      <span className="text-red-600 ml-2">
                        ({testResults.schemaValidation.summary.missingFields} missing)
                      </span>
                    )}
                  </div>
                </div>

                {/* Individual API Validation */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Team Members API */}
                  {testResults.schemaValidation.apis.teamMembers && !testResults.schemaValidation.apis.teamMembers.error && (
                    <div className="bg-white rounded border p-3">
                      <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        Team Members API
                      </h5>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Coverage:</span>
                          <span className={`font-medium ${
                            testResults.schemaValidation.apis.teamMembers.coverage >= 90 ? 'text-green-600' : 
                            testResults.schemaValidation.apis.teamMembers.coverage >= 75 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {testResults.schemaValidation.apis.teamMembers.coverage}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mapped:</span>
                          <span className="text-green-600">{testResults.schemaValidation.apis.teamMembers.mappedFields}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Missing:</span>
                          <span className="text-red-600">{testResults.schemaValidation.apis.teamMembers.missingFields?.length || 0}</span>
                        </div>
                        {testResults.schemaValidation.apis.teamMembers.missingFields?.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            Missing: {testResults.schemaValidation.apis.teamMembers.missingFields.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Daily Usage API */}
                  {testResults.schemaValidation.apis.dailyUsage && !testResults.schemaValidation.apis.dailyUsage.error && (
                    <div className="bg-white rounded border p-3">
                      <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Daily Usage API
                      </h5>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Coverage:</span>
                          <span className={`font-medium ${
                            testResults.schemaValidation.apis.dailyUsage.coverage >= 90 ? 'text-green-600' : 
                            testResults.schemaValidation.apis.dailyUsage.coverage >= 75 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {testResults.schemaValidation.apis.dailyUsage.coverage}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mapped:</span>
                          <span className="text-green-600">{testResults.schemaValidation.apis.dailyUsage.mappedFields}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Missing:</span>
                          <span className="text-red-600">{testResults.schemaValidation.apis.dailyUsage.missingFields?.length || 0}</span>
                        </div>
                        {testResults.schemaValidation.apis.dailyUsage.missingFields?.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            Missing: {testResults.schemaValidation.apis.dailyUsage.missingFields.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Usage Events API */}
                  {testResults.schemaValidation.apis.usageEvents && !testResults.schemaValidation.apis.usageEvents.error && (
                    <div className="bg-white rounded border p-3">
                      <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <PlayCircle className="w-4 h-4 text-orange-600" />
                        Usage Events API
                      </h5>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Coverage:</span>
                          <span className={`font-medium ${
                            testResults.schemaValidation.apis.usageEvents.coverage >= 90 ? 'text-green-600' : 
                            testResults.schemaValidation.apis.usageEvents.coverage >= 75 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {testResults.schemaValidation.apis.usageEvents.coverage}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mapped:</span>
                          <span className="text-green-600">{testResults.schemaValidation.apis.usageEvents.mappedFields}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Missing:</span>
                          <span className="text-red-600">{testResults.schemaValidation.apis.usageEvents.missingFields?.length || 0}</span>
                        </div>
                        {testResults.schemaValidation.apis.usageEvents.missingFields?.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            Missing: {testResults.schemaValidation.apis.usageEvents.missingFields.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Schema Validation Error */}
                {Object.values(testResults.schemaValidation.apis).some(api => api.error) && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <h5 className="font-medium text-yellow-800 mb-1">Validation Warnings</h5>
                    <div className="text-sm text-yellow-700 space-y-1">
                      {testResults.schemaValidation.apis.teamMembers?.error && (
                        <div>• Team Members: {testResults.schemaValidation.apis.teamMembers.error}</div>
                      )}
                      {testResults.schemaValidation.apis.dailyUsage?.error && (
                        <div>• Daily Usage: {testResults.schemaValidation.apis.dailyUsage.error}</div>
                      )}
                      {testResults.schemaValidation.apis.usageEvents?.error && (
                        <div>• Usage Events: {testResults.schemaValidation.apis.usageEvents.error}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Clear Results Button */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setTestResults(null)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <Trash2 className="w-4 h-4" />
                Clear Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CursorApiManager;